import React from 'react';
import { useForm } from 'react-hook-form';
import {
  EstatePlanningGoalDataDTO,
  FORM_ID,
  FormValues,
  UPSERT_GOAL_URL,
} from './EstatePlanning.meta';
import { GoalComponentProps as Props } from '../../components/Goal/Goal.meta';
import { useGoalsQuery } from '../../api/useGoalsQuery';
import { useUpsertGoalMutation } from '../../api/useUpsertGoalMutation';
import { UpsertGoalModal } from '../../components/UpsertGoalModal/UpsertGoalModal';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { AssetBucketingSelect } from '../../components/AssetBucketingSelect/AssetBucketingSelect';
import { useGoalFormDisabled } from '../../hooks/useGoalFormDisabled';
import { useFeatureFlag } from '../../../../_hooks/useFeatureFlag';
import { Feature } from '../../../../CommonSelectors/CommonArea.types';
import { EstatePlanningForm } from './components/EstatePlanningForm/EstatePlanningForm';
import { NonEditableGoal } from './components/NonEditableGoal/NonEditableGoal';

export const EstatePlanning = (props: Props<EstatePlanningGoalDataDTO>) => {
  const isEstatePlanningEnabled = useFeatureFlag(Feature.EnableEstatePlanning);

  if (!isEstatePlanningEnabled) {
    return <EstatePlanningContent {...props} />;
  }

  return <EstatePlanningForm {...props} />;
};

const EstatePlanningContent = (props: Props<EstatePlanningGoalDataDTO>) => {
  const { clientId } = props;

  const { data } = useGoalsQuery({ clientId });

  if (!data) {
    return null;
  }

  const {
    assetBucketingOptions: { enableGoalAssetBucketing },
  } = data;

  if (!enableGoalAssetBucketing) {
    return <NonEditableGoal {...props} />;
  }

  return <EditableGoal {...props} />;
};

const EditableGoal = ({ clientId, onClose, assetBucketingIndices, ...rest }: Props) => {
  const readOnly = useGoalFormDisabled();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      assetBucketingIndices,
    },
    disabled: readOnly,
  });

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: UPSERT_GOAL_URL });

  const onSubmit = async (values: FormValues) => {
    try {
      await upsertGoal(values);

      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <UpsertGoalModal
      clientId={clientId}
      formId={FORM_ID}
      isLoading={isUpsertingGoal}
      onClose={onClose}
      {...rest}
    >
      {upsertGoalError && <Alert message={upsertGoalError.message} variant="error" mb="16" />}

      <Text color="neutral.500" mb="16">
        View the Estate Planning Module within the Goal Results section. You can optionally assign
        this goal to one or more asset buckets.
      </Text>

      <Box
        id={FORM_ID}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        aria-label="Estate Planning goal form"
      >
        <AssetBucketingSelect clientId={clientId} control={control} />
      </Box>
    </UpsertGoalModal>
  );
};
